/* eslint-disable no-param-reassign */
import { createSelector, PayloadAction } from '@reduxjs/toolkit'

import { get } from 'utils/lodash'

import { BooleanPreferences } from './cardVisibilityStorage'

// Shared card-visibility machinery for the route-manager boards. The master and
// board-view-v2 slices both persist a set of `with<Attribute>` booleans toggled
// through the same CardFilterMenu surface; this factors out the identical part
// (state shape, the two generic reducers, the selectors). Each slice keeps its
// own `createSlice` call and persisted `name`/state path — the master slice also
// carries layout + map-layer toggles, so it can't be a bare factory output.

export type CardToggleSliceKey<K extends string> = `with${Capitalize<K>}`

export type CardVisibilityState<K extends string> = Record<CardToggleSliceKey<K>, boolean>

export const toCardSliceKey = <K extends string>(attrKey: K): CardToggleSliceKey<K> =>
  `with${attrKey.charAt(0).toUpperCase()}${attrKey.slice(1)}` as CardToggleSliceKey<K>

interface CardVisibilityConfig<K extends string> {
  readonly statePath: string
  readonly toggleKeys: readonly K[]
  // Selectors fall back to these when persisted state predates a newly-added key.
  readonly defaults: CardVisibilityState<K>
}

// Writable view of the templated `with<Key>` slots so the reducers can assign
// to them (TS won't narrow an index on the generic state type).
type WritableCardVisibility<K extends string> = { [P in CardToggleSliceKey<K>]: boolean }

export interface CardVisibilityReducers<S extends CardVisibilityState<K>, K extends string> {
  setCardAttributeVisible: (state: S, action: PayloadAction<{ key: K; visible: boolean } | undefined>) => void
  setAllCardAttributesVisible: (state: S, action: PayloadAction<boolean>) => void
  hydrateFromStorage: (state: S, action: PayloadAction<BooleanPreferences | undefined>) => void
  restoreFromStorage: (state: S, action: PayloadAction<BooleanPreferences | undefined>) => void
}

export const createCardVisibilityReducers = <S extends CardVisibilityState<K>, K extends string>(
  toggleKeys: readonly K[],
  // Keys that drive a refetch — applyStopsToGrid replaces gridData with BE truth, dropping unpublished board edits.
  fetchAffectingKeys: readonly K[] = []
): CardVisibilityReducers<S, K> => ({
  setCardAttributeVisible: (state, action) => {
    const { payload } = action
    if (!payload) return
    const { key, visible } = payload
    if (!key || !toggleKeys.includes(key)) return
    const toggles = state as WritableCardVisibility<K>
    toggles[toCardSliceKey(key)] = Boolean(visible)
  },
  setAllCardAttributesVisible: (state, action) => {
    const visible = Boolean(action.payload)
    const toggles = state as WritableCardVisibility<K>
    toggleKeys.forEach(key => {
      toggles[toCardSliceKey(key)] = visible
    })
  },
  // Startup only: a store this fresh has no in-flight work, so the whole slice comes back.
  restoreFromStorage: (state, action) => {
    const { payload } = action
    if (!payload) return
    const toggles = state as Record<string, boolean>
    Object.keys(state).forEach(sliceKey => {
      if (typeof payload[sliceKey] === 'boolean' && toggles[sliceKey] !== payload[sliceKey]) toggles[sliceKey] = payload[sliceKey]
    })
  },
  // Card keys only — withEnded/withDeactivated feed the publish payload; reading them back could change what a publish sends.
  hydrateFromStorage: (state, action) => {
    const { payload } = action
    if (!payload) return
    const toggles = state as Record<string, boolean>
    toggleKeys.forEach(key => {
      if (fetchAffectingKeys.includes(key)) return
      const sliceKey = toCardSliceKey(key)
      if (typeof payload[sliceKey] === 'boolean' && toggles[sliceKey] !== payload[sliceKey]) toggles[sliceKey] = payload[sliceKey]
    })
  },
})

export interface CardVisibilitySelectors<K extends string> {
  // `state: unknown` so these stay assignable into each slice's narrower
  // `Selector<boolean>` (a selector taking a wider input is a subtype).
  selectWith: (attrKey: K) => (state: unknown) => boolean
  selectCardAttributeVisibility: (state: unknown) => Record<K, boolean>
}

export const createCardVisibilitySelectors = <K extends string>({
  statePath,
  toggleKeys,
  defaults,
}: CardVisibilityConfig<K>): CardVisibilitySelectors<K> => {
  const selectWith =
    (attrKey: K) =>
    (state: unknown): boolean => {
      const sliceKey = toCardSliceKey(attrKey)
      return Boolean(get(state, `${statePath}.${sliceKey}`, defaults[sliceKey]))
    }

  // Memoized on the persisted slice, so the {key: visible} map keeps a stable
  // reference until a toggle actually changes — cards subscribed to it then
  // share one reference check per dispatch instead of rebuilding the map.
  const selectCardAttributeVisibility = createSelector(
    (state: unknown) => get(state, statePath),
    slice =>
      toggleKeys.reduce<Record<K, boolean>>((acc, key) => {
        const sliceKey = toCardSliceKey(key)
        acc[key] = Boolean(get(slice, sliceKey, defaults[sliceKey]))
        return acc
      }, {} as Record<K, boolean>)
  )

  return { selectWith, selectCardAttributeVisibility }
}
